import { useState } from 'react';

type CardType = 'credit' | 'check';

interface CardFormData {
  cardNumber1: string;
  cardNumber2: string;
  cardNumber3: string;
  cardNumber4: string;
  expiryMonth: string;
  expiryYear: string;
  cvc: string;
  cardholderName: string;
  birthDate: string;
}

export type { CardFormData };

interface CardRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRegister: (cardType: CardType, formData: CardFormData) => void;
  saving?: boolean;
  success?: boolean;
}

export default function CardRegistrationModal({
  isOpen,
  onClose,
  onRegister,
  saving = false,
  success = false,
}: CardRegistrationModalProps) {
  const [cardType, setCardType] = useState<CardType>('credit');
  const [step, setStep] = useState<1 | 2>(1);
  const [agreedAutoPay, setAgreedAutoPay] = useState(false);
  const [formData, setFormData] = useState<CardFormData>({
    cardNumber1: '',
    cardNumber2: '',
    cardNumber3: '',
    cardNumber4: '',
    expiryMonth: '',
    expiryYear: '',
    cvc: '',
    cardholderName: '',
    birthDate: '',
  });

  if (!isOpen) return null;

  function updateForm(field: keyof CardFormData, value: string) {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }

  function isStep1Valid() {
    return agreedAutoPay;
  }

  function isStep2Valid() {
    return (
      formData.cardNumber1.length === 4 &&
      formData.cardNumber2.length === 4 &&
      formData.cardNumber3.length === 4 &&
      formData.cardNumber4.length === 4 &&
      formData.expiryMonth.length === 2 &&
      formData.expiryYear.length === 2 &&
      formData.cvc.length >= 3 &&
      formData.cardholderName.trim().length > 0 &&
      formData.birthDate.length === 6
    );
  }

  function handleSubmit() {
    if (!isStep2Valid()) return;
    onRegister(cardType, formData);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 p-0 md:p-4" onClick={onClose}>
      <div
        className="bg-white w-full max-w-md rounded-t-3xl md:rounded-3xl max-h-[90vh] md:max-h-[85vh] overflow-y-auto animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="sticky top-0 bg-white z-10 px-6 pt-6 pb-4 border-b border-gray-50">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-extrabold text-gray-900">결제 카드 등록</h2>
            <button
              onClick={onClose}
              disabled={saving}
              className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors disabled:opacity-40"
            >
              <i className="ri-close-line text-gray-500" />
            </button>
          </div>
          <p className="text-xs text-gray-400">간병비 자동결제를 위한 카드를 등록해주세요</p>

          {/* 단계 표시 */}
          <div className="flex items-center gap-2 mt-4">
            <div
              className={`flex items-center gap-2 ${step === 1 ? 'opacity-100' : 'opacity-50'}`}
            >
              <div
                className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-extrabold ${
                  step === 1
                    ? 'text-white'
                    : step === 2
                    ? 'bg-[#E8F5E9] text-[#2E7D6B]'
                    : 'bg-gray-100 text-gray-400'
                }`}
                style={step === 1 ? { backgroundColor: '#2E7D6B' } : {}}
              >
                {step === 2 ? <i className="ri-check-line text-[10px]" /> : '1'}
              </div>
              <span className={`text-xs font-bold ${step === 1 ? 'text-gray-900' : 'text-gray-400'}`}>
                안내 확인
              </span>
            </div>
            <div className="flex-1 h-px bg-gray-200" />
            <div className={`flex items-center gap-2 ${step === 2 ? 'opacity-100' : 'opacity-50'}`}>
              <div
                className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-extrabold ${
                  step === 2
                    ? 'text-white'
                    : 'bg-gray-100 text-gray-400'
                }`}
                style={step === 2 ? { backgroundColor: '#2E7D6B' } : {}}
              >
                2
              </div>
              <span className={`text-xs font-bold ${step === 2 ? 'text-gray-900' : 'text-gray-400'}`}>
                카드 정보
              </span>
            </div>
          </div>
        </div>

        {/* Step 1: 안내 확인 */}
        {step === 1 && (
          <div className="p-6 space-y-5">
            {/* 카드 종류 선택 */}
            <div>
              <label className="block text-xs font-extrabold text-gray-700 mb-2">
                카드 종류 선택
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => setCardType('credit')}
                  className={`flex-1 py-3.5 rounded-xl border-2 text-sm font-bold cursor-pointer transition-all ${
                    cardType === 'credit'
                      ? 'border-[#2E7D6B] text-[#2E7D6B] bg-[#E8F5E9]'
                      : 'border-gray-100 text-gray-500 bg-white hover:border-gray-200'
                  }`}
                >
                  <i className="ri-bank-card-line mr-1.5" />
                  신용카드
                </button>
                <button
                  onClick={() => setCardType('check')}
                  className={`flex-1 py-3.5 rounded-xl border-2 text-sm font-bold cursor-pointer transition-all ${
                    cardType === 'check'
                      ? 'border-[#2E7D6B] text-[#2E7D6B] bg-[#E8F5E9]'
                      : 'border-gray-100 text-gray-500 bg-white hover:border-gray-200'
                  }`}
                >
                  <i className="ri-bank-card-2-line mr-1.5" />
                  체크카드
                </button>
              </div>
            </div>

            {/* 주단위 자동결제 안내 */}
            <div className="bg-amber-50 rounded-2xl p-5 border border-amber-200 space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                  <i className="ri-calendar-check-line text-amber-600" />
                </div>
                <div>
                  <p className="text-xs font-extrabold text-amber-800">주단위 결제 · 매주 월요일 자동결제</p>
                  <p className="text-[10px] text-amber-600">실제 근무 일수만큼 정확하게 정산됩니다</p>
                </div>
              </div>

              <div className="space-y-2.5">
                <div className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-[10px] font-extrabold text-amber-700">1</span>
                  </div>
                  <p className="text-xs text-amber-800 leading-relaxed">
                    <strong className="text-amber-900">첫 주</strong>는 간병 시작일부터 일요일까지의 <strong className="text-amber-900">실제 근무 일수</strong>만큼만 결제됩니다. 예: 수요일 시작 → 수~일 5일분 결제
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-[10px] font-extrabold text-amber-700">2</span>
                  </div>
                  <p className="text-xs text-amber-800 leading-relaxed">
                    <strong className="text-amber-900">이후 매주</strong> 월~일 <strong className="text-amber-900">7일분</strong>이 매주 월요일에 자동 결제됩니다.
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-[10px] font-extrabold text-amber-700">3</span>
                  </div>
                  <p className="text-xs text-amber-800 leading-relaxed">
                    <strong className="text-amber-900">마지막 주</strong>는 월요일부터 종료일까지 <strong className="text-amber-900">실제 근무 일수</strong>만큼만 결제됩니다. 예: 수요일 종료 → 월~수 3일분 결제
                  </p>
                </div>
              </div>

              <div className="pt-2 border-t border-amber-200">
                <p className="text-[10px] text-amber-600 leading-relaxed">
                  결제 전 SMS로 사전 안내되며, 등록된 카드로 매주 월요일 자동 출금됩니다.
                </p>
              </div>
            </div>

            {/* PG사 보안 안내 */}
            <div className="rounded-2xl border border-emerald-100 overflow-hidden" style={{ backgroundColor: '#f0fdf6' }}>
              <div className="flex items-center gap-2 px-5 py-3 border-b border-emerald-100" style={{ backgroundColor: '#e6f9f0' }}>
                <i className="ri-shield-check-fill text-lg" style={{ color: '#2E7D6B' }} />
                <span className="text-sm font-extrabold" style={{ color: '#1a4d3d' }}>안전한 PG사 보안 결제</span>
              </div>
              <div className="px-5 py-4">
                <p className="text-xs text-gray-700 leading-relaxed">
                  카드 정보는 <strong style={{ color: '#1a4d3d' }}>국내 최상위 PG사</strong>의 보안 시스템에서만 암호화 처리되며, <strong style={{ color: '#1a4d3d' }}>코리아케어러스는 카드 정보를 저장하거나 보관하지 않습니다.</strong> 모든 결제는 PCI-DSS 국제 보안 인증을 준수하는 암호화 통신으로 안전하게 처리됩니다.
                </p>
              </div>
            </div>

            {/* 동의 체크 */}
            <div className="space-y-3">
              <label className="flex items-start gap-3 p-4 rounded-xl border border-gray-100 bg-white cursor-pointer hover:border-[#2E7D6B]/30 transition-colors">
                <input
                  type="checkbox"
                  checked={agreedAutoPay}
                  onChange={(e) => setAgreedAutoPay(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 text-[#2E7D6B] focus:ring-[#2E7D6B] accent-[#2E7D6B]"
                />
                <div>
                  <p className="text-sm font-bold text-gray-900">
                    주단위 자동결제 동의
                    <span className="text-red-400 ml-0.5">*</span>
                  </p>
                  <p className="text-[11px] text-gray-500 mt-0.5 leading-relaxed">
                    매주 월요일 간병비가 등록된 카드로 자동 결제되는 것에 동의합니다. 결제 전 SMS로 사전 안내됩니다.
                  </p>
                </div>
              </label>

              {/* 약관 링크 */}
              <p className="text-[11px] text-gray-400 text-center leading-relaxed pt-1">
                등록하신 카드 정보는{' '}
                <a href="/app/terms" className="text-gray-600 font-semibold underline hover:text-[#2E7D6B] transition-colors whitespace-nowrap">이용약관</a>
                {' '}및{' '}
                <a href="/app/privacy" className="text-gray-600 font-semibold underline hover:text-[#2E7D6B] transition-colors whitespace-nowrap">개인정보처리방침</a>
                {' '}에 따라 안전하게 관리됩니다.
              </p>
            </div>

            {/* 다음 버튼 */}
            <button
              onClick={() => setStep(2)}
              disabled={!isStep1Valid()}
              className="w-full py-3.5 rounded-xl text-sm font-bold text-white cursor-pointer transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 whitespace-nowrap"
              style={{ backgroundColor: '#2E7D6B' }}
            >
              다음 단계로
              <i className="ri-arrow-right-line ml-1.5" />
            </button>
          </div>
        )}

        {/* Step 2: 카드 정보 입력 */}
        {step === 2 && (
          <div className="p-6 space-y-5">
            {/* 카드 미리보기 */}
            <div className="rounded-2xl p-5 text-white relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' }}>
              <div className="absolute top-3 right-4">
                <div className="flex gap-1">
                  <div className="w-6 h-6 rounded-full bg-white/20" />
                  <div className="w-6 h-6 rounded-full bg-white/20 -ml-2" />
                </div>
              </div>
              <p className="text-[10px] text-white/60 font-medium mb-6">코리아케어러스 결제카드</p>
              <p className="text-lg font-mono tracking-widest mb-3">
                {formData.cardNumber1 || '••••'} {formData.cardNumber2 || '••••'} {formData.cardNumber3 || '••••'} {formData.cardNumber4 || '••••'}
              </p>
              <div className="flex items-center gap-6">
                <div>
                  <p className="text-[8px] text-white/50 mb-0.5">유효기간</p>
                  <p className="text-xs font-mono">
                    {formData.expiryMonth || 'MM'}/{formData.expiryYear || 'YY'}
                  </p>
                </div>
                <div>
                  <p className="text-[8px] text-white/50 mb-0.5">소유자</p>
                  <p className="text-xs font-medium">{formData.cardholderName || 'CARDHOLDER NAME'}</p>
                </div>
              </div>
            </div>

            {/* 선택된 카드 타입 요약 */}
            <div className="flex items-center gap-3 p-3 rounded-xl bg-[#F8F9FA]">
              <div className="w-10 h-10 rounded-xl bg-[#E8F5E9] flex items-center justify-center shrink-0">
                <i className={`${cardType === 'credit' ? 'ri-bank-card-line' : 'ri-bank-card-2-line'} text-[#2E7D6B]`} />
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold text-gray-900">
                  {cardType === 'credit' ? '신용카드' : '체크카드'}
                </p>
                <p className="text-[10px] text-gray-400">매주 월요일 자동결제</p>
              </div>
              <button
                onClick={() => setStep(1)}
                className="text-[11px] text-gray-400 hover:text-gray-600 cursor-pointer underline whitespace-nowrap"
              >
                변경
              </button>
            </div>

            {/* 카드번호 */}
            <div>
              <label className="block text-xs font-extrabold text-gray-700 mb-2">
                카드번호 <span className="text-red-400">*</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  maxLength={4}
                  value={formData.cardNumber1}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '');
                    updateForm('cardNumber1', v);
                    if (v.length === 4) document.getElementById('cardNum2')?.focus();
                  }}
                  placeholder="0000"
                  className="w-full px-3 py-3 rounded-xl border border-gray-200 text-sm text-center font-mono tracking-widest outline-none focus:border-[#2E7D6B] bg-white"
                />
                <span className="text-gray-300 text-sm">-</span>
                <input
                  id="cardNum2"
                  type="text"
                  maxLength={4}
                  value={formData.cardNumber2}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '');
                    updateForm('cardNumber2', v);
                    if (v.length === 4) document.getElementById('cardNum3')?.focus();
                  }}
                  placeholder="0000"
                  className="w-full px-3 py-3 rounded-xl border border-gray-200 text-sm text-center font-mono tracking-widest outline-none focus:border-[#2E7D6B] bg-white"
                />
                <span className="text-gray-300 text-sm">-</span>
                <input
                  id="cardNum3"
                  type="text"
                  maxLength={4}
                  value={formData.cardNumber3}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '');
                    updateForm('cardNumber3', v);
                    if (v.length === 4) document.getElementById('cardNum4')?.focus();
                  }}
                  placeholder="0000"
                  className="w-full px-3 py-3 rounded-xl border border-gray-200 text-sm text-center font-mono tracking-widest outline-none focus:border-[#2E7D6B] bg-white"
                />
                <span className="text-gray-300 text-sm">-</span>
                <input
                  id="cardNum4"
                  type="text"
                  maxLength={4}
                  value={formData.cardNumber4}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '');
                    updateForm('cardNumber4', v);
                  }}
                  placeholder="0000"
                  className="w-full px-3 py-3 rounded-xl border border-gray-200 text-sm text-center font-mono tracking-widest outline-none focus:border-[#2E7D6B] bg-white"
                />
              </div>
            </div>

            {/* 유효기간 + CVC */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-extrabold text-gray-700 mb-2">
                  유효기간 (MM/YY) <span className="text-red-400">*</span>
                </label>
                <div className="flex items-center gap-1.5">
                  <input
                    type="text"
                    maxLength={2}
                    value={formData.expiryMonth}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '');
                      if (parseInt(v) > 12 || v.length > 2) return;
                      updateForm('expiryMonth', v);
                      if (v.length === 2) document.getElementById('expiryYear')?.focus();
                    }}
                    placeholder="MM"
                    className="w-full px-3 py-3 rounded-xl border border-gray-200 text-sm text-center font-mono outline-none focus:border-[#2E7D6B] bg-white"
                  />
                  <span className="text-gray-300 text-sm">/</span>
                  <input
                    id="expiryYear"
                    type="text"
                    maxLength={2}
                    value={formData.expiryYear}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '');
                      updateForm('expiryYear', v);
                    }}
                    placeholder="YY"
                    className="w-full px-3 py-3 rounded-xl border border-gray-200 text-sm text-center font-mono outline-none focus:border-[#2E7D6B] bg-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-extrabold text-gray-700 mb-2">
                  CVC <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <input
                    type="password"
                    maxLength={4}
                    value={formData.cvc}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '');
                      updateForm('cvc', v);
                    }}
                    placeholder="•••"
                    className="w-full px-3 py-3 rounded-xl border border-gray-200 text-sm text-center font-mono tracking-widest outline-none focus:border-[#2E7D6B] bg-white"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer" title="카드 뒷면 3자리 숫자">
                    <i className="ri-question-line text-gray-300 text-sm" />
                  </div>
                </div>
              </div>
            </div>

            {/* 카드 소유자 */}
            <div>
              <label className="block text-xs font-extrabold text-gray-700 mb-2">
                카드 소유자 <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={formData.cardholderName}
                onChange={(e) => updateForm('cardholderName', e.target.value.toUpperCase())}
                placeholder="카드에 표기된 영문 이름"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm uppercase outline-none focus:border-[#2E7D6B] bg-white placeholder:text-gray-300"
              />
              <p className="text-[10px] text-gray-400 mt-1">카드 앞면에 표기된 영문 이름을 입력해주세요</p>
            </div>

            {/* 생년월일 (사업자번호) */}
            <div>
              <label className="block text-xs font-extrabold text-gray-700 mb-2">
                생년월일 (YYMMDD) <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                maxLength={6}
                value={formData.birthDate}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '');
                  updateForm('birthDate', v);
                }}
                placeholder="예: 900101"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#2E7D6B] bg-white"
              />
              <p className="text-[10px] text-gray-400 mt-1">
                {cardType === 'credit' ? '개인 신용카드의 경우 생년월일 6자리, 법인카드의 경우 사업자번호 10자리를 입력해주세요' : '생년월일 6자리를 입력해주세요'}
              </p>
            </div>

            {/* 보안 안내 */}
            <div className="rounded-2xl border border-emerald-100 overflow-hidden" style={{ backgroundColor: '#f0fdf6' }}>
              <div className="flex items-center gap-2 px-5 py-3 border-b border-emerald-100" style={{ backgroundColor: '#e6f9f0' }}>
                <i className="ri-shield-check-fill text-lg" style={{ color: '#2E7D6B' }} />
                <span className="text-sm font-extrabold" style={{ color: '#1a4d3d' }}>카드 정보는 PG사에서 안전하게 보호합니다</span>
              </div>
              <div className="px-5 py-4 space-y-2.5">
                <div className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: '#d4f5e3' }}>
                    <span className="text-[10px] font-extrabold" style={{ color: '#2E7D6B' }}>1</span>
                  </div>
                  <p className="text-xs text-gray-700 leading-relaxed">
                    입력하신 카드 정보는 <strong style={{ color: '#1a4d3d' }}>국내 최상위 PG사(결제 대행사)</strong>의 보안 서버로 직접 전송되어 암호화됩니다.
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: '#d4f5e3' }}>
                    <span className="text-[10px] font-extrabold" style={{ color: '#2E7D6B' }}>2</span>
                  </div>
                  <p className="text-xs text-gray-700 leading-relaxed">
                    <strong style={{ color: '#1a4d3d' }}>코리아케어러스는 카드 정보를 저장하거나 보관하지 않습니다.</strong> 카드번호, CVC, 유효기간 등 모든 결제 민감 정보는 PG사 시스템 내에서만 안전하게 관리됩니다.
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: '#d4f5e3' }}>
                    <span className="text-[10px] font-extrabold" style={{ color: '#2E7D6B' }}>3</span>
                  </div>
                  <p className="text-xs text-gray-700 leading-relaxed">
                    PG사는 <strong style={{ color: '#1a4d3d' }}>PCI-DSS 국제 보안 인증</strong>을 준수하며, 모든 카드 결제는 암호화된 통신으로 처리되어 외부 유출 위험이 없습니다.
                  </p>
                </div>
              </div>
            </div>

            {/* 액션 버튼 */}
            <div className="flex gap-2">
              <button
                onClick={() => setStep(1)}
                disabled={saving}
                className="flex-1 py-3.5 rounded-xl text-sm font-bold text-gray-500 bg-gray-100 cursor-pointer hover:bg-gray-200 transition-colors disabled:opacity-40 whitespace-nowrap"
              >
                이전
              </button>
              <button
                onClick={handleSubmit}
                disabled={!isStep2Valid() || saving}
                className="flex-[2] py-3.5 rounded-xl text-sm font-bold text-white cursor-pointer transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 whitespace-nowrap"
                style={{ backgroundColor: (!isStep2Valid() || saving) ? '#B0BEC5' : '#2E7D6B' }}
              >
                {saving ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    등록 중...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-1.5">
                    <i className="ri-check-line" />
                    카드 등록 완료
                  </span>
                )}
              </button>
            </div>
          </div>
        )}

        {/* 등록 완료 화면 */}
        {success && (
          <div className="p-8 text-center space-y-5">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto" style={{ backgroundColor: '#e6f9f0' }}>
              <i className="ri-check-fill text-3xl" style={{ color: '#2E7D6B' }} />
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-gray-900 mb-2">카드 등록이 완료되었습니다</h3>
              <p className="text-xs text-gray-500 leading-relaxed">
                간병비는 매주 월요일 등록하신 카드로 자동 결제되며,<br />
                결제 전 SMS로 사전 안내해 드립니다.
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-full py-3.5 rounded-xl text-sm font-bold text-white cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap"
              style={{ backgroundColor: '#2E7D6B' }}
            >
              확인
            </button>
          </div>
        )}

        <style>{`
          @keyframes slide-up {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
          .animate-slide-up {
            animation: slide-up 0.3s ease-out;
          }
        `}</style>
      </div>
    </div>
  );
}